// src/services/api.js
import axios from 'axios';
import {jwtDecode} from 'jwt-decode';
import {type} from "@testing-library/user-event/dist/type";


console.log('API_URL from env:', process.env.REACT_APP_API_URL); // Debug line
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const apiClient = axios.create({
                                   baseURL: API_URL,
                                   headers: {
                                       'Content-Type': 'application/json',
                                       'Accept': 'application/json',
                                   },
                               });

// Add a request interceptor to include the auth token and timezone in requests
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Add client timezone to all requests
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            config.headers['X-Timezone'] = timezone;
        } catch (error) {
            // Fallback to Arizona timezone if browser doesn't support timezone detection
            config.headers['X-Timezone'] = 'America/Phoenix';
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add a response interceptor to handle token expiration
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already tried to refresh the token
        if (error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            console.log(error.response);

            try {
                // Attempt to refresh the token
                const refreshResponse = await apiClient.post('/refresh');
                const { token } = refreshResponse.data;

                // Store the new token
                localStorage.setItem('token', token);

                // Update the auth header for the original request
                originalRequest.headers['Authorization'] = `Bearer ${token}`;

                // Retry the original request
                return apiClient(originalRequest);
            } catch (refreshError) {
                console.log('refreshError', refreshError);
                // If refresh fails, log out the user
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Authentication services
export const authService = {
    login: async (credentials) => {
        const response = await apiClient.post('/login', credentials);
        if (response.data && response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data));
        }
        return response.data;
    },

    register: async (userData) => {
        const response = await apiClient.post('/register', userData);
        if (response.data && response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data));
        }
        return response.data;
    },

    logout: async () => {
        await apiClient.post('/logout');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    isTokenValid: () => {
        const token = localStorage.getItem('token');
        if (!token) return false;

        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            return decoded.exp > currentTime;
        } catch (error) {
            return false;
        }
    },

    hasRole: (role) => {
        const user = authService.getCurrentUser();
        if (!user || !user.roles) return false;

        return user.roles.some(userRole => userRole.name === role);
    },

    hasAnyRole: (roles) => {
        const user = authService.getCurrentUser();
        if (!user || !user.roles) return false;

        return user.roles.some(userRole => roles.includes(userRole.name));
    }
};

// AI Query services
export const aiService = {
    askQuestion: async (question, context = {}) => {
        try {
            const response = await apiClient.post('/ai/ask', {
                question,
                context
            });
            return response.data;
        } catch (error) {
            console.error('Error asking AI question:', error);
            throw error;
        }
    },

    executeQuery: async (query) => {
        try {
            const response = await apiClient.post('/ai/query', { query });
            return response.data;
        } catch (error) {
            console.error('Error executing query:', error);
            throw error;
        }
    },

    validateQuery: async (query) => {
        try {
            const response = await apiClient.post('/ai/validate', { query });
            return response.data;
        } catch (error) {
            console.error('Error validating query:', error);
            throw error;
        }
    },

    generateInsight: async (prompt) => {
        try {
            const response = await apiClient.post('/ai/insight', { prompt });
            return response.data;
        } catch (error) {
            console.error('Error generating AI insight:', error);
            throw error;
        }
    },

    getAvailableMetrics: async () => {
        try {
            const response = await apiClient.get('/ai/metrics');
            return response.data;
        } catch (error) {
            console.error('Error fetching available metrics:', error);
            throw error;
        }
    }
};

// Dashboard service
export const dashboardService = {
    getDashboardData: async () => {
        const response = await apiClient.get('/dashboard');
        return response.data;
    }
};

// Inventory services
export const inventoryService = {
    getInventory: async () => {
        const response = await apiClient.get('/inventory');
        return response.data;
    },

    stockIn: async (data) => {
        const response = await apiClient.post('/inventory/stock-in', data);
        return response.data;
    },

    stockOut: async (data) => {
        const response = await apiClient.post('/inventory/stock-out', data);
        return response.data;
    },

    adjustInventory: async (data) => {
        const response = await apiClient.post('/inventory/adjust', data);
        return response.data;
    },

    getTransactions: async (params) => {
        const response = await apiClient.get('/inventory/transactions', { params });
        return response.data;
    },

    getPartsToOrder: async () => {
        const response = await apiClient.get('/inventory/parts-to-order');
        return response.data;
    },

    generateCutSheet: async () => {
        const response = await apiClient.get('/inventory/generate-cut-sheet', {
            responseType: 'blob'
        });
        return response;
    },

    exportInventoryReport: async (params) => {
        const response = await apiClient.get('/inventory/export-inventory-report', {
            params,
            responseType: 'blob'
        });
        return response;
    }
};

// Product services
export const productService = {
    getProducts: async () => {
        const response = await apiClient.get('/products');
        return response.data;
    },

    getProductsAndVariants: async () => {
        const response = await apiClient.get('/products-and-variants');
        return response.data;
    },

    getProduct: async (id) => {
        const response = await apiClient.get(`/products/${id}`);
        return response.data;
    },

    getVariantOptions: async () => {
        const response = await apiClient.get(`/variant-options`);
        return response.data;
    },

    createProduct: async (data) => {
        const response = await apiClient.post('/products', data);
        return response.data;
    },

    updateProduct: async (id, data) => {
        const response = await apiClient.put(`/products/${id}`, data);
        return response.data;
    },

    deleteProduct: async (id) => {
        const response = await apiClient.delete(`/products/${id}`);
        return response.data;
    },

    // Delete a specific product variant
    deleteProductVariant: async (id, variant) => {
        const response = await apiClient.delete(`/products/${id}/variants/${variant}`);
        return response.data;
    },
};

// Part Type services
export const partTypeService = {
    getPartTypes: async () => {
        const response = await apiClient.get('/part-types');
        return response.data;
    },

    getPartType: async (id) => {
        const response = await apiClient.get(`/part-types/${id}`);
        return response.data;
    },

    createPartType: async (data) => {
        const response = await apiClient.post('/part-types', data);
        return response.data;
    },

    updatePartType: async (id, data) => {
        const response = await apiClient.put(`/part-types/${id}`, data);
        return response.data;
    },

    deletePartType: async (id) => {
        const response = await apiClient.delete(`/part-types/${id}`);
        return response.data;
    }
};

// Stock services
export const stockService = {
    getStock: async (params) => {
        const response = await apiClient.get('/stock', { params });
        return response.data;
    },

    getStockItem: async (id) => {
        const response = await apiClient.get(`/stock/${id}`);
        return response.data;
    },

    addStock: async (data) => {
        const response = await apiClient.post('/stock/add', data);
        return response.data;
    },

    adjustStock: async (data) => {
        const response = await apiClient.post('/stock/adjust', data);
        return response.data;
    },

    cancelOrderAllocations: async (orderId) => {
        const response = await apiClient.post(`/stock/orders/${orderId}/cancel-allocations`);
        return response.data;
    },

    shipOrderAllocations: async (orderId) => {
        const response = await apiClient.post(`/stock/orders/${orderId}/ship-allocations`);
        return response.data;
    }
};


// Customer services - Add this to your services/api.js file
// Customer services - Add this to your services/api.js file
export const customerService = {
    /**
     * Get a list of all customers with optional filtering
     * @param {Object} filters - Optional filters (search, from_date, to_date, sort_by, sort_order)
     * @returns {Promise<Object>} - Promise resolving to customers data
     */
    getCustomers: async (filters = {}) => {
        const queryParams = new URLSearchParams();

        // Add filters to query string
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.customer_type) queryParams.append('customer_type', filters.customer_type);
        if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
        if (filters.from_date) queryParams.append('from_date', filters.from_date);
        if (filters.to_date) queryParams.append('to_date', filters.to_date);
        if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
        if (filters.sort_order) queryParams.append('sort_order', filters.sort_order);
        if (filters.per_page) queryParams.append('per_page', filters.per_page);
        if (filters.page) queryParams.append('page', filters.page);

        const queryString = queryParams.toString();
        const url = `/customers${queryString ? `?${queryString}` : ''}`;

        try {
            const response = await apiClient.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error;
        }
    },

    /**
     * Create a new customer
     * @param {Object} customerData - Customer data
     * @returns {Promise<Object>} - Promise resolving to created customer
     */
    createCustomer: async (customerData) => {
        try {
            const response = await apiClient.post('/customers', customerData);
            return response.data;
        } catch (error) {
            console.error('Error creating customer:', error);
            throw error;
        }
    },

    /**
     * Get a single customer by ID
     * @param {string|number} customerId - Customer ID
     * @returns {Promise<Object>} - Promise resolving to customer data
     */
    getCustomer: async (customerId) => {
        try {
            const response = await apiClient.get(`/customers/${customerId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching customer ${customerId}:`, error);
            throw error;
        }
    },

    /**
     * Update customer information
     * @param {string|number} customerId - Customer ID
     * @param {Object} customerData - Updated customer data
     * @returns {Promise<Object>} - Promise resolving to updated customer
     */
    updateCustomer: async (customerId, customerData) => {
        try {
            const response = await apiClient.put(`/customers/${customerId}`, customerData);
            return response.data;
        } catch (error) {
            console.error(`Error updating customer ${customerId}:`, error);
            throw error;
        }
    },

    /**
     * Delete/deactivate a customer
     * @param {string|number} customerId - Customer ID
     * @returns {Promise<Object>} - Promise resolving to success message
     */
    deleteCustomer: async (customerId) => {
        try {
            const response = await apiClient.delete(`/customers/${customerId}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting customer ${customerId}:`, error);
            throw error;
        }
    },

    /**
     * Get all orders for a specific customer
     * @param {string|number} customerId - Customer ID
     * @param {Object} filters - Optional filters (status, from_date, to_date, per_page)
     * @returns {Promise<Object>} - Promise resolving to customer orders
     */
    getCustomerOrders: async (customerId, filters = {}) => {
        const queryParams = new URLSearchParams();

        if (filters.status) queryParams.append('status', filters.status);
        if (filters.from_date) queryParams.append('from_date', filters.from_date);
        if (filters.to_date) queryParams.append('to_date', filters.to_date);
        if (filters.per_page) queryParams.append('per_page', filters.per_page);
        if (filters.page) queryParams.append('page', filters.page);

        const queryString = queryParams.toString();
        const url = `/customers/${customerId}/orders${queryString ? `?${queryString}` : ''}`;

        try {
            const response = await apiClient.get(url);
            return response.data;
        } catch (error) {
            console.error(`Error fetching orders for customer ${customerId}:`, error);
            throw error;
        }
    },

    /**
     * Get customer analytics/insights
     * @param {string|number} customerId - Customer ID
     * @returns {Promise<Object>} - Promise resolving to customer analytics
     */
    getCustomerAnalytics: async (customerId) => {
        try {
            const response = await apiClient.get(`/customers/${customerId}/analytics`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching analytics for customer ${customerId}:`, error);
            throw error;
        }
    },

    /**
     * Get customer lifetime value and segmentation data
     * @param {string|number} customerId - Customer ID
     * @returns {Promise<Object>} - Promise resolving to customer lifetime value data
     */
    getCustomerLifetime: async (customerId) => {
        try {
            const response = await apiClient.get(`/customers/${customerId}/lifetime`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching lifetime value for customer ${customerId}:`, error);
            throw error;
        }
    },

    /**
     * Search customers across multiple fields
     * @param {string} query - Search query string
     * @param {number} limit - Maximum number of results (default 20)
     * @returns {Promise<Array>} - Promise resolving to array of matching customers
     */
    searchCustomers: async (query, limit = 20) => {
        const queryParams = new URLSearchParams();
        queryParams.append('query', query);
        queryParams.append('limit', limit);

        try {
            const response = await apiClient.get(`/customers/search?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error searching customers:', error);
            throw error;
        }
    }
};

// Order services
export const orderService = {
    getOrders: async (params) => {
        const response = await apiClient.get('/orders', { params });
        return response.data;
    },

    getOrder: async (id) => {
        const response = await apiClient.get(`/orders/${id}`);
        return response.data;
    },

    createOrder: async (data) => {
        const response = await apiClient.post('/orders', data);
        return response.data;
    },

    updateOrder: async (id, data) => {
        const response = await apiClient.put(`/orders/${id}`, data);
        return response.data;
    },

    deleteOrder: async (id) => {
        const response = await apiClient.delete(`/orders/${id}`);
        return response.data;
    },

    checkFulfillability: async (id) => {
        const response = await apiClient.get(`/orders/${id}/check-fulfillability`);
        return response.data;
    },

    processOrder: async (id) => {
        const response = await apiClient.post(`/orders/${id}/process`);
        return response.data;
    }
};

// Production services
// Update to the productionService object in services/api.js

export const productionService = {
    // Existing methods
    getProductionBatches: async (filters = {}) => {
        try {
            const response = await apiClient.get('/production', { params: filters });
            return response.data;
        } catch (error) {
            console.error('Error fetching production batches:', error);
            throw error;
        }
    },


    getProductionBatch: async (id) => {
        try {
            const response = await apiClient.get(`/production/${id}`);
            return response.data.data;
        } catch (error) {
            throw error;
        }
    },

    createProductionBatch: async (batchData) => {
        try {
            const response = await apiClient.post('/production', batchData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateProductionBatch: async (id, batchData) => {
        try {
            const response = await apiClient.put(`/production/${id}`, batchData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    deleteProductionBatch: async (id, options = {}) => {
        try {
            if (options.force_delete !== undefined) {
                options.force_delete = options.force_delete ? 1 : 0;
            }

            console.log("ForceDelete", typeof options.force_delete);
            const response = await apiClient.delete(`/production/${id}`, { params: options });
            return response.data;
        } catch (error) {
            throw error;
        }
    },



    allocateMaterials: async (id) => {
        try {
            const response = await apiClient.post(`/production/${id}/allocate-materials`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // New method for single item allocation
    allocateItemMaterials: async (batchId, itemId) => {
        try {
            const response = await apiClient.post(`/production/${batchId}/allocate-item-materials/${itemId}`);
            return response.data;
        } catch (error) {
            console.error('Error allocating materials for item:', error);
            throw error;
        }
    },

    completeItem: async (batchId, itemId) => {
        try {
            const response = await apiClient.post(`/production/${batchId}/complete-item/${itemId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // New methods for production workflow
    startItemProduction: async (batchId, itemId) => {
        try {
            const response = await apiClient.post(`/production/${batchId}/start-item/${itemId}`);
            return response.data;
        } catch (error) {
            console.error('Error starting production item:', error);
            throw error;
        }
    },

    updateItemCompletion: async (batchId, itemId, data) => {
        try {
            const response = await apiClient.post(`/production/${batchId}/update-item-completion/${itemId}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating item completion:', error);
            throw error;
        }
    },


    checkProductParts: async (productId, quantity, variant = 'standard') => {
        try {
            const url = variant === 'standard'
                        ? `/production/inventory/check-product-parts/${productId}/${quantity}`
                        : `/production/inventory/check-product-parts/${productId}/${quantity}/${variant}`;

            const response = await apiClient.get(url);
            return response.data;
        } catch (error) {
            console.error('Error checking product parts availability:', error);
            throw error;
        }
    },

    checkNewShipstationBatches: async () => {
        try {
            const response = await apiClient.get('/production/shipstation/check-new-batches');
            return response.data;
        } catch (error) {
            console.error('Error checking new ShipStation batches:', error);
            throw error;
        }
    },

    importShipstationBatch: async (data) => {
        try {
            const response = await apiClient.post('/production/shipstation/import-batch', data);
            return response.data;
        } catch (error) {
            console.error('Error importing ShipStation batch:', error);
            throw error;
        }
    },

    importBatchItems: async (batchId) => {
        try {
            const response = await apiClient.post(`/production/batches/${batchId}/import-items`);
            return response.data;
        } catch (error) {
            console.error('Error importing batch items:', error);
            throw error;
        }
    },
};

// Packing Slip services
export const packingSlipService = {
    getPackingSlips: async (filters = {}) => {
        try {
            const response = await apiClient.get('/packing-slips', { params: filters });
            return response.data;
        } catch (error) {
            console.error('Error fetching packing slips:', error);
            throw error;
        }
    },

    getBatchesWithoutSlips: async () => {
        try {
            const response = await apiClient.get('/packing-slips/batches-without-slips');
            return response.data;
        } catch (error) {
            console.error('Error fetching batches without slips:', error);
            throw error;
        }
    },

    getPackingSlip: async (id) => {
        try {
            const response = await apiClient.get(`/packing-slips/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching packing slip:', error);
            throw error;
        }
    },

    createPackingSlip: async (data) => {
        try {
            const response = await apiClient.post('/packing-slips', data);
            return response.data;
        } catch (error) {
            console.error('Error creating packing slip:', error);
            throw error;
        }
    },

    updatePackingSlip: async (id, data) => {
        try {
            const response = await apiClient.put(`/packing-slips/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating packing slip:', error);
            throw error;
        }
    },

    deletePackingSlip: async (id) => {
        try {
            const response = await apiClient.delete(`/packing-slips/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting packing slip:', error);
            throw error;
        }
    }
};

// Part Order services

export const partOrderService = {
    /**
     * Get part type requirements needed for ordering
     * This calculates the requirements based on pending orders and current inventory levels
     * @returns {Promise<Array>} - Promise resolving to array of part type requirements
     */
    getSupplyOrder: async () => {
        try {
            const response = await apiClient.get('/part-orders/supply-orders');
            return response.data.data;
        } catch (error) {
            console.error('Error fetching part type requirements:', error);
            throw error;
        }
    },

    getForecastedSupplyOrder: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();

            if (params.forecast_months) queryParams.append('forecast_months', params.forecast_months);
            if (params.growth_rate) queryParams.append('growth_rate', params.growth_rate);
            if (params.lookback_months) queryParams.append('lookback_months', params.lookback_months);

            const queryString = queryParams.toString();
            const url = `/part-orders/forecast-orders${queryString ? `?${queryString}` : ''}`;

            const response = await apiClient.get(url);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching forecasted supply requirements:', error);
            throw error;
        }
    },

    getProductFinancialForecast: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();

            if (params.forecast_months) queryParams.append('forecast_months', params.forecast_months);
            if (params.growth_rate) queryParams.append('growth_rate', params.growth_rate);
            if (params.lookback_months) queryParams.append('lookback_months', params.lookback_months);

            const queryString = queryParams.toString();
            const url = `/part-orders/product-financial-forecast${queryString ? `?${queryString}` : ''}`;

            const response = await apiClient.get(url);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching forecasted supply requirements:', error);
            throw error;
        }
    },

    /**
     * Get a list of part orders with optional filtering
     * @param {Object} filters - Optional filters (status, search, supplier, from_date, to_date)
     * @returns {Promise<Object>} - Promise resolving to response data
     */
    getPartOrders: async (filters = {}) => {
        const queryParams = new URLSearchParams();

        // Add filters to query string
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.supplier) queryParams.append('supplier', filters.supplier);
        if (filters.from_date) queryParams.append('from_date', filters.from_date);
        if (filters.to_date) queryParams.append('to_date', filters.to_date);

        const queryString = queryParams.toString();
        const url = `/part-orders${queryString ? `?${queryString}` : ''}`;

        try {
            const response = await apiClient.get(url);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching part orders:', error);
            throw error;
        }
    },

    /**
     * Get a single part order by ID
     * @param {string|number} id - Part order ID
     * @returns {Promise<Object>} - Promise resolving to part order data
     */
    getPartOrder: async (id) => {
        try {
            const response = await apiClient.get(`/part-orders/${id}`);
            return response.data.data;
        } catch (error) {
            console.error(`Error fetching part order ${id}:`, error);
            throw error;
        }
    },

    /**
     * Create a new part order
     * @param {Object} orderData - Part order data
     * @returns {Promise<Object>} - Promise resolving to created part order
     */
    createPartOrder: async (orderData) => {
        try {
            const response = await apiClient.post('/part-orders', orderData);
            return response.data;
        } catch (error) {
            console.error('Error creating part order:', error);
            throw error;
        }
    },

    /**
     * Update an existing part order
     * @param {string|number} id - Part order ID
     * @param {Object} orderData - Updated part order data
     * @returns {Promise<Object>} - Promise resolving to updated part order
     */
    updatePartOrder: async (id, orderData) => {
        try {
            const response = await apiClient.put(`/part-orders/${id}`, orderData);
            return response.data;
        } catch (error) {
            console.error(`Error updating part order ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a part order
     * @param {string|number} id - Part order ID
     * @returns {Promise<Object>} - Promise resolving to success message
     */
    deletePartOrder: async (id) => {
        try {
            const response = await apiClient.delete(`/part-orders/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting part order ${id}:`, error);
            throw error;
        }
    },

    /**
     * Receive items for a part order
     * @param {string|number} id - Part order ID
     * @param {Array} items - Array of items to receive with quantities
     * @param {string} notes - Optional notes about the receipt
     * @returns {Promise<Object>} - Promise resolving to updated part order
     */
    receiveItems: async (id, items, notes = '') => {
        try {
            const response = await apiClient.post(`/part-orders/${id}/receive-items`, {
                items,
                notes
            });
            return response.data;
        } catch (error) {
            console.error(`Error receiving items for part order ${id}:`, error);
            throw error;
        }
    },

    /**
     * Get suggested order based on inventory thresholds
     * @returns {Promise<Object>} - Promise resolving to suggested order data
     */
    suggestOrder: async () => {
        try {
            const response = await apiClient.get('/part-orders/suggest');
            return response.data.data;
        } catch (error) {
            console.error('Error getting suggested order:', error);
            throw error;
        }
    },

    /**
     * Generate cut sheet data based on current inventory and pending orders
     * @returns {Promise<Object>} - Promise resolving to cut sheet data
     */
    generateCutSheet: async () => {
        try {
            const response = await apiClient.get('/part-orders/cut-sheet');
            return response.data.data;
        } catch (error) {
            console.error('Error generating cut sheet:', error);
            throw error;
        }
    },

    /**
     * Create orders automatically from cut sheet data
     * @param {Object} options - Optional settings for order creation
     * @returns {Promise<Object>} - Promise resolving to created orders
     */
    createOrdersFromCutSheet: async (options = {}) => {
        try {
            const response = await apiClient.post('/part-orders/create-from-cut-sheet', options);
            return response.data;
        } catch (error) {
            console.error('Error creating orders from cut sheet:', error);
            throw error;
        }
    }
};

// User services
export const userService = {
    getUsers: async () => {
        const response = await apiClient.get('/users');
        return response.data;
    },

    getMe: async () => {
        const response = await apiClient.get(`/user`);
        return response.data;
    },

    getUser: async (id) => {
        const response = await apiClient.get(`/users/${id}`);
        return response.data;
    },

    createUser: async (data) => {
        const response = await apiClient.post('/users', data);
        return response.data;
    },

    updateUser: async (id, data) => {
        const response = await apiClient.put(`/users/${id}`, data);
        return response.data;
    },

    deleteUser: async (id) => {
        const response = await apiClient.delete(`/users/${id}`);
        return response.data;
    },

    updateNotificationPreferences: async (uid, data) => {
        const response = await apiClient.put(`/users/${uid}/notification-preferences`, data);
        return response.data;
    },
    getRoles: async (data) => {
        const response = await apiClient.get('/roles', data);
        return response.data;
    }
};

// Vendor services
export const vendorService = {
    /**
     * Get a list of all vendors
     * @param {Object} filters - Optional filters
     * @returns {Promise<Array>} - Promise resolving to array of vendors
     */
    getVendors: async (filters = {}) => {
        const queryParams = new URLSearchParams();

        // Add filters to query string if needed
        if (filters.isActive !== undefined) queryParams.append('is_active', filters.isActive);
        if (filters.search) queryParams.append('search', filters.search);

        const queryString = queryParams.toString();
        const url = `/vendors${queryString ? `?${queryString}` : ''}`;

        try {
            const response = await apiClient.get(url);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching vendors:', error);
            throw error;
        }
    },

    /**
     * Get a single vendor by ID
     * @param {string|number} id - Vendor ID
     * @returns {Promise<Object>} - Promise resolving to vendor data
     */
    getVendor: async (id) => {
        try {
            const response = await apiClient.get(`/vendors/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching vendor ${id}:`, error);
            throw error;
        }
    },

    /**
     * Get all parts for a specific vendor
     * @param {string|number} vendorId - Vendor ID
     * @returns {Promise<Array>} - Promise resolving to array of parts
     */
    getVendorParts: async (vendorId) => {
        try {
            const response = await apiClient.get(`/vendors/${vendorId}/parts`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching parts for vendor ${vendorId}:`, error);
            throw error;
        }
    },

    /**
     * Create a new vendor
     * @param {Object} vendorData - Vendor data
     * @returns {Promise<Object>} - Promise resolving to created vendor
     */
    createVendor: async (vendorData) => {
        try {
            const response = await apiClient.post('/vendors', vendorData);
            return response.data;
        } catch (error) {
            console.error('Error creating vendor:', error);
            throw error;
        }
    },

    /**
     * Update an existing vendor
     * @param {string|number} id - Vendor ID
     * @param {Object} vendorData - Updated vendor data
     * @returns {Promise<Object>} - Promise resolving to updated vendor
     */
    updateVendor: async (id, vendorData) => {
        try {
            const response = await apiClient.put(`/vendors/${id}`, vendorData);
            return response.data;
        } catch (error) {
            console.error(`Error updating vendor ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a vendor
     * @param {string|number} id - Vendor ID
     * @returns {Promise<Object>} - Promise resolving to success message
     */
    deleteVendor: async (id) => {
        try {
            const response = await apiClient.delete(`/vendors/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting vendor ${id}:`, error);
            throw error;
        }
    }
};

// Settings services
export const settingsService = {
    getSettings: async (category) => {
        const response = await apiClient.get(`/settings/${category}`);
        return response.data;
    },

    updateSettings: async (category, data) => {
        // Handle FormData for file uploads (like company logo)
        if (data instanceof FormData) {
            const response = await apiClient.put(`/settings/${category}`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        }

        // Handle regular JSON data
        const response = await apiClient.put(`/settings/${category}`, data);
        return response.data;
    },

    testIntegration: async (integration) => {
        const response = await apiClient.post(`/settings/integrations/${integration}/test`);
        return response.data;
    }
};

// Location services
export const locationService = {
    getLocations: async () => {
        const response = await apiClient.get('/locations');
        return response.data;
    },

    getLocation: async (id) => {
        const response = await apiClient.get(`/locations/${id}`);
        return response.data;
    },

    createLocation: async (data) => {
        const response = await apiClient.post('/locations', data);
        return response.data;
    },

    updateLocation: async (id, data) => {
        const response = await apiClient.put(`/locations/${id}`, data);
        return response.data;
    },

    deleteLocation: async (id) => {
        const response = await apiClient.delete(`/locations/${id}`);
        return response.data;
    },

    setDefaultLocation: async (id) => {
        const response = await apiClient.post(`/locations/${id}/default`);
        return response.data;
    }
};

// Import services
export const importService = {
    importProducts: async (formData) => {
        const response = await apiClient.post('/import/products', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },
    importInventory: async (formData) => {
        const response = await apiClient.post('/import/inventory', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    }
};

// Shopify services
export const shopifyService = {
    syncOrders: async (data) => {
        const response = await apiClient.post('/shopify/sync-orders', data);
        return response.data;
    }
};

// Reports services
export const reportsService = {
    inventoryStatus: async (params = {}) => {
        const response = await apiClient.post('/reports/inventory-status', params);
        return response.data;
    },

    salesAnalysis: async (params) => {
        const response = await apiClient.post('/reports/sales-analysis', params);
        return response.data;
    },

    productionPerformance: async (params) => {
        const response = await apiClient.post('/reports/production-performance', params);
        return response.data;
    },

    purchasingAnalysis: async (params) => {
        const response = await apiClient.post('/reports/purchasing-analysis', params);
        return response.data;
    },

    financialSummary: async (params) => {
        const response = await apiClient.post('/reports/financial-summary', params);
        return response.data;
    }
};

// Export the api client as the main api instance
export const api = apiClient;
export default apiClient;
